/**
 * "Suporte e segurança" on the person record (D580): the support tickets the person is part of and the safety incidents they reported
 * or were the subject of — the history an agent opens first when the person calls. Two reads, each saying on its own when it could
 * not be read. A ticket opens its case; an incident has no record of its own, so the card offers the safety queue.
 */
import { type ReactElement } from 'react';
import { type DirectoryUser } from '@vello/api-client';
import { Badge, Button, Ink, RecordPanel, RecordRow, RecordRowButton, Text } from '@vello/web-theme';
import { screenAllowed } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { CardSection, useCardRead } from './person-card';
import { usePersonFormat } from './person-format';

const SUPPORT_LIMIT = 10;

const ticketTone = (status: string): 'success' | 'warning' | 'neutral' => (status === 'RESOLVED' || status === 'CLOSED' ? 'success' : status === 'OPEN' || status === 'REOPENED' ? 'warning' : 'neutral');
const incidentTone = (status: string): 'success' | 'danger' | 'warning' | 'neutral' => (status === 'RESOLVED' || status === 'CLOSED' ? 'success' : status === 'ESCALATED' ? 'danger' : status === 'OPEN' ? 'warning' : 'neutral');

export function PersonSupportCard({ user }: { user: DirectoryUser }): ReactElement {
  const { runtime, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const { v } = useVocab();
  const fmt = usePersonFormat();
  const tickets = useCardRead(() => runtime.personInsights.supportTickets(user.id, { limit: SUPPORT_LIMIT }), [runtime, user.id]);
  const incidents = useCardRead(() => runtime.personInsights.safetyIncidents(user.id, { limit: SUPPORT_LIMIT }), [runtime, user.id]);
  const casesOpen = screenAllowed(grants, 'case_detail');

  return (
    <RecordPanel
      title={t('prec.support.title')}
      actions={
        screenAllowed(grants, 'safety') ? (
          <Button variant="ghost" size="sm" onClick={() => navigate('safety')}>
            {t('prec.support.safetyQueue')}
          </Button>
        ) : undefined
      }
    >
      <CardSection
        title={t('prec.support.tickets')}
        what={t('prec.what.tickets')}
        state={tickets.state}
        reload={tickets.reload}
        isEmpty={(r) => r.tickets.length === 0}
        empty={t('prec.support.tickets.empty')}
      >
        {(r) => (
          <>
            {r.tickets.map((x) => {
              const body = (
                <>
                  <Ink strong>{x.categoryCode !== undefined ? v('support.support_category', x.categoryCode) : t('prec.support.noCategory')}</Ink>
                  <Badge tone={ticketTone(x.status)}>{v('support.ticket_status', x.status)}</Badge>
                  {x.subjectKind !== undefined && (
                    <Text role="caption" tone="secondary">
                      {v('support.ticket_subject_kind', x.subjectKind)}
                    </Text>
                  )}
                  <span className="vello-grow" />
                  <Ink mono>{fmt.when(x.createdAt)}</Ink>
                </>
              );
              return casesOpen ? (
                <RecordRowButton key={x.ticketId} aria-label={t('prec.support.open')} onClick={() => navigate('case_detail', { id: x.ticketId })}>
                  {body}
                </RecordRowButton>
              ) : (
                <RecordRow key={x.ticketId} dense>
                  {body}
                </RecordRow>
              );
            })}
          </>
        )}
      </CardSection>

      <CardSection
        title={t('prec.support.incidents')}
        what={t('prec.what.incidents')}
        state={incidents.state}
        reload={incidents.reload}
        isEmpty={(r) => r.incidents.length === 0}
        empty={t('prec.support.incidents.empty')}
      >
        {(r) => (
          <>
            {r.incidents.map((x) => (
              <RecordRow key={x.incidentId} dense>
                <Ink strong>{v('safety.incident_type', x.kindCode)}</Ink>
                <Badge tone={incidentTone(x.status)}>{v('safety.incident_status', x.status)}</Badge>
                <Text role="caption" tone="secondary">
                  {v('safety.incident_role', x.role)}
                </Text>
                {x.severity !== undefined && (
                  <Text role="caption" tone="secondary">
                    {v('safety.severity', x.severity)}
                  </Text>
                )}
                <span className="vello-grow" />
                <Ink mono>{fmt.when(x.createdAt)}</Ink>
              </RecordRow>
            ))}
          </>
        )}
      </CardSection>
    </RecordPanel>
  );
}
